import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  TextField as MuiTextField,
  styled,
} from "@mui/material";
import MainLayout from "../../layouts/main/main_layout";
import { SearchRounded } from "@mui/icons-material";
import FilterServices from "./components/filter_services/filter_services";
import DatGridServices from "./components/datagrid/datagrid_services";
import Loading from "../../components/loading/loading";
import { useFeedback } from "../../providers/feedback/feeedback";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getStateMasterData } from "../../services/master/master_service";
import { ServiceList } from "./model/service_model";
import {
  deleteService,
  downloadService,
  getFilterList,
  getServiceList,
  getStatusList,
  updateService,
} from "../../services/services/services";
import { queryClient } from "../../providers/query/query";
import { downloadFromBlob } from "../../utils/utils";
import UpdateServiceStatusToReject from "./components/update_service_status/update_service_status_popup";
import UpdateServiceStatusToDelete from "./components/update_service_status/update_service_status_popup";
import useUser from "../../providers/user/user_provider";
import ServiceLayout from "../../layouts/service/service_layout";
import AccessDenied from "../../components/access_denied/access_denied";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../constants";
import { useServicesFilterStore } from "../../store/use-services-filter-store";
import { servicesLabels } from "./constants/service-labels";
import { greyEnabled, purple } from "../../theme/colors";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import SortServicesPopup, {
  servicesSortOptionToParams,
} from "./components/sort_services_popup/sort_services_popup";
import ApproveServicePopup from "./components/approve_service_popup/approve_service_popup";

export interface ServicesLabel {
  label: string;
  flex?: number;
}

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function Services() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackbar } = useFeedback();
  const {
    openModal,
    stateId,
    cityId,
    districtId,
    categoryId,
    selectedStatus,
    searchValue,
    startDate,
    endDate,
    ageFrom,
    ageTo,
    gender,
    sortOption,
    page,
    pageSize,
    setField,
  } = useServicesFilterStore();

  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [approveServiceDialog, setApproveServiceDialog] = useState<boolean>(false);
  const [closeRejectServiceStatusDialog, setCloseRejectServiceStatusDialog] =
    useState<boolean>(false);
  const [closeDeleteServiceStatusDialog, setCloseDeleteServiceStatusDialog] =
    useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<ServiceList | null>(
    null
  );
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [dialogSubmitting, setDialogSubmitting] = useState(false);
  const { hasPermission } = useUser();
  const { selectedOrgId } = useSelectedOrgStore();

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setField('page', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  useEffect(() => {
    setField('page', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, sortOption, pageSize]);

  const { data: services, isFetching } = useQuery(
  [
    "services",
    page,
    pageSize,
    sortOption,
    categoryId,
    cityId,
    districtId,
    stateId,
    selectedStatus,
    debouncedSearchValue,
    startDate,
    endDate,
    ageFrom,
    ageTo,
    gender,
    selectedOrgId,
  ],
  async () => {
    try {
      const { sortBy, sortOrder } = servicesSortOptionToParams(sortOption);
      const response = await getServiceList({
        size: pageSize,
        page,
        category: categoryId,
        city: cityId,
        district: districtId,
        state: stateId,
        status: selectedStatus,
        keyword: debouncedSearchValue,
        startDate,
        endDate,
        ageFrom,
        ageTo,
        gender,
        sortBy,
        sortOrder,
        orgId: selectedOrgId,
      });

      return response?.data?.data?.data ?? { list: [], total: 0 };
    } catch (error) {
      console.error("Failed to fetch services:", error);
      // return a safe empty object so components don’t break
      return { list: [], total: 0 };
    }
  },
  {
    retry: false,
  }
);


  // These three only feed the Filter dialog's dropdowns — no need to fetch
  // them until the admin actually opens it.
  const { data: masterDataList } = useQuery(
    ["servicesStateMasterList"],
    async () => {
      let response = await getStateMasterData();
      return response?.data?.data?.list;
    },
    { enabled: openModal }
  );

  const { data: statusList } = useQuery(
    ["statusList", selectedOrgId],
    async () => {
      let response = await getStatusList(selectedOrgId);
      return response.data.data.list;
    },
    { enabled: openModal }
  );

  const { data: filterList } = useQuery(
    ["filterList", selectedOrgId],
    async () => {
      let response = await getFilterList(selectedOrgId);
      return response.data.data;
    },
    { enabled: openModal }
  );


  function updateServiceApiCall({
    statusId,
    serviceId,
  }: {
    statusId: number;
    serviceId: number;
  }) {
    setApprovingId(serviceId);
    updateService({
      comment: "",
      statusId: statusId,
      serviceId: serviceId,
    })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Service status updated successfully");
          queryClient.invalidateQueries({ queryKey: ["services"] });
          setApproveServiceDialog(false);
        }
      })
      .catch((e) => {
        showSnackbar(
          e.response?.status === 403
            ? "You do not have permission to perform this operation."
            : e?.response?.data?.error?.message ||
                "Something went wrong while updating the service status."
        );
      })
      .finally(() => setApprovingId(null));
  }
  function deleteServiceApiCall({ comments }: { comments: string }) {
    setDialogSubmitting(true);
    deleteService({ serviceId: +selectedUserId?.id!, comment: comments })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Service removed successfully");
          queryClient.invalidateQueries({ queryKey: ["services"] });
          setCloseDeleteServiceStatusDialog(false);
        }
      })
      .catch((e) => {
        showSnackbar(
          e.response?.status === 403
            ? "You do not have permission to perform this operation."
            : e?.response?.data?.error?.message ||
                "Something went wrong while removing the service."
        );
      })
      .finally(() => setDialogSubmitting(false));
  }

  function updateServiceStatus(comments: string, statusId: number) {
    setDialogSubmitting(true);
    updateService({
      comment: comments,
      statusId: statusId,
      serviceId: +selectedUserId?.id!,
    })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Service status updated successfully");
          queryClient.invalidateQueries({ queryKey: ["services"] });
          setCloseRejectServiceStatusDialog(false);
        }
      })
      .catch((e) => {
        showSnackbar(
          e.response?.status === 403
            ? "You do not have permission to perform this operation."
            : e?.response?.data?.error?.message ||
                "Something went wrong while updating the service status."
        );
      })
      .finally(() => setDialogSubmitting(false));
  }

  function downloadExcel() {
    const { sortBy, sortOrder } = servicesSortOptionToParams(sortOption);
    if (services?.total <= 1000) {
      downloadService({
        page: 1,
        size: 1000,
        category: categoryId,
        city: cityId,
        district: districtId,
        state: stateId,
        status: selectedStatus,
        startDate: startDate,
        endDate: endDate,
        ageFrom: ageFrom,
        ageTo: ageTo,
        gender: gender,
        format: "excel",
        sortBy,
        sortOrder,
        orgId: selectedOrgId
      }).then((response) => {
        downloadFromBlob(response, new Date().valueOf() + ".xlsx");
      });
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
  }

  useEffect(() => {
    const currentPage = searchParams.get("page");
    const newPage = (page).toString();

    if (currentPage !== newPage) {
      setSearchParams({ page: newPage }, { replace: true, });
    }

  }, [page, setSearchParams]);

  useEffect(() => {
    setField('page', searchParams.has("page") ? parseInt(searchParams?.get("page")!) : 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function searchComponent() {
    return (
      <Box px={{ xs: 2.5, sm: 3 }} pt={2.5}>
        <TextField
          value={searchValue}
          onChange={(e) => {
            setField('searchValue', e.target.value);
          }}
          size="small"
          fullWidth
          sx={{ maxWidth: 420 }}
          placeholder="Search by service name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    );
  }
  return (
    <MainLayout>
      {hasPermission("services", "read") ? (
        <ServiceLayout
          downloadExcel={downloadExcel}
          setOpenModal={(val)=>{ setField('openModal',val)}}
          sortOption={sortOption}
          onOpenSort={() => setSortDialogOpen(true)}
          page={page}
          setPage={(page: number) => {
            setField('page', page);
          }}
        >
          {searchComponent()}
          {isFetching ? (
            <Box
              display="flex"
              justifyContent={"center"}
              alignItems={"center"}
              height="100%"
            >
              <Loading />
            </Box>
          ) : (
            <DatGridServices
              labels={servicesLabels}
              data={services?.list || []}
              count={services?.total | 0}
              page={page - 1}
              setPage={(page: number) => {
                setField('page', page);
              }} 
            
              setSelectedUserId={setSelectedUserId}
              approvingId={approvingId}
              setApproveServiceDialog={setApproveServiceDialog}
              setCloseRejectServiceStatusDialog={
                setCloseRejectServiceStatusDialog
              }
              setCloseDeleteServiceStatusDialog={
                setCloseDeleteServiceStatusDialog
              }
            />
          )}
        </ServiceLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
      <SortServicesPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={(option) => setField('sortOption', option)}
      />
      <FilterServices
        states={masterDataList || []}
        category={filterList?.categories || []}
        statusList={statusList}
        genderList={filterList?.gender}
      // setPage={setPage}
      />
      {approveServiceDialog && (
        <ApproveServicePopup
          open={approveServiceDialog}
          serviceName={selectedUserId?.name}
          submitting={approvingId === selectedUserId?.id}
          onClose={() => setApproveServiceDialog(false)}
          onConfirm={() => {
            if (selectedUserId?.id) {
              updateServiceApiCall({ statusId: 3, serviceId: +selectedUserId.id });
            }
          }}
        />
      )}
      {closeRejectServiceStatusDialog && (
        <UpdateServiceStatusToReject
          open={closeRejectServiceStatusDialog}
          submitting={dialogSubmitting}
          onDialogClose={() => {
            setCloseRejectServiceStatusDialog(false);
          }}
          updateServiceStatus={(comments) => {
            updateServiceStatus(comments, 4);
          }}
          title={"Reject Service"}
          subtitle={"Reason for rejection:"}
        />
      )}
      {closeDeleteServiceStatusDialog && (
        <UpdateServiceStatusToDelete
          open={closeDeleteServiceStatusDialog}
          submitting={dialogSubmitting}
          onDialogClose={() => {
            setCloseDeleteServiceStatusDialog(false);
          }}
          updateServiceStatus={(comments) => {
            deleteServiceApiCall({ comments: comments });
          }}
          title={"Delete Service"}
          subtitle={"Reason for Deletion:"}
        />
      )}
    </MainLayout>
  );
}